/* global Modernizr */

import { useState, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useParams, useHistory } from 'react-router-dom';
import { enotaryActions, httpActions } from '@figure/frontlib-onboarding-redux';
import {
  usePersonalLoan,
  usePersonalLoanRouteFsm,
  useFeatureDetection,
  useRedux,
  useRedirect,
} from 'shared/hooks';
import BrowserSupportModal from './BrowserSupportModal';
import FundingSession from './FundingSession';

const USER_MEDIA_CHECK = Modernizr.getusermedia;
let timeoutId;
let positionTimeoutId;

const MAX_PULL_ATTEMPTS = 3;
const overrideAction = httpActions.overrideCreator(() => ({
  message: 'Connecting to a Customer Success Associate',
}));

const FundingSessionContainer = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { uuid: personalLoanUuid } = useParams();
  const { personalLoan, error: personalLoanError } = usePersonalLoan();
  const { checkFeatures } = useFeatureDetection([USER_MEDIA_CHECK]);
  const {
    accept,
    cancel,
    disconnect,
    getPosition,
    notarySession,
    loading: enotaryLoading,
    error: enotaryError,
  } = useRedux('enotaryReducer', enotaryActions);
  const runRouter = usePersonalLoanRouteFsm();
  const { redirectToDashboard } = useRedirect();

  const [featuresMissing, setFeaturesMissing] = useState(false);
  const [twilioError, setTwilioError] = useState('');
  const [connected, setConnected] = useState(false);

  const serverError = personalLoanError || enotaryError || '';
  const { primaryParticipantAcceptsComplete = false, primaryParticipantAcceptsReady = false } =
    personalLoan?.status ?? {};

  const pullNotaryConnection = (dispatch, personalLoanUuid, count = 0) => {
    if (timeoutId === -1) return Promise.resolve('No longer mounted');
    if (count >= MAX_PULL_ATTEMPTS) return Promise.reject('Unable to connect with enotary service');
    dispatch(overrideAction());

    return new Promise((resolve) => {
      timeoutId = window.setTimeout(() => {
        resolve(
          dispatch(enotaryActions.connect(personalLoanUuid))
            .then(() => Promise.resolve(dispatch(httpActions.complete())))
            .catch((e) =>
              Promise.resolve(dispatch(httpActions.complete())).then(() =>
                e
                  ? pullNotaryConnection(dispatch, personalLoanUuid, count + 1)
                  : Promise.reject('No error response, most likely cancellation')
              )
            )
        );
      }, 2000);
    });
  };

  const pullNotaryPosition = (dispatch) => {
    if (positionTimeoutId === -1) return Promise.resolve('No longer mounted');

    return new Promise((resolve) => {
      positionTimeoutId = window.setTimeout(() => {
        resolve(
          dispatch(enotaryActions.getPosition())
            .then(() => {
              if (!connected) {
                pullNotaryPosition(dispatch);
              }
            })
            .catch((e) =>
              e
                ? pullNotaryPosition(dispatch)
                : Promise.reject('No error response, most likely cancellation')
            )
        );
      }, 5000);
    });
  };

  const initialize = async () => {
    if (!primaryParticipantAcceptsReady || primaryParticipantAcceptsComplete) {
      return runRouter();
    }

    try {
      await pullNotaryConnection(dispatch, personalLoanUuid);
      await getPosition();
      await pullNotaryPosition(dispatch);
    } catch (e) {
      window.Rollbar.error('FundingSession - initialize: ', e);
      redirectToDashboard();
    }
  };
  useEffect(() => {
    timeoutId = positionTimeoutId = undefined;
    const passedFeatureChecks = checkFeatures([USER_MEDIA_CHECK]).payload.some(
      (featureTest) => !!featureTest
    );

    if (passedFeatureChecks) {
      initialize();
    } else {
      setFeaturesMissing(true);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(
    () => () => {
      disconnect();
      if (timeoutId) {
        window.clearTimeout(timeoutId);
        timeoutId = -1;
      }

      if (positionTimeoutId) {
        window.clearTimeout(positionTimeoutId);
        positionTimeoutId = -1;
      }
    },
    [disconnect]
  );

  const handleConnected = useCallback(() => {
    window.clearTimeout(positionTimeoutId);
    setConnected(true);
  }, [setConnected]);

  const handleAccept = (data) => {
    const [activeDocument] = notarySession.documents;
    const [activeParticipant] = activeDocument.participants;
    accept({
      documentUuid: activeDocument.documentUuid,
      notarySessionDocumentParticipantUuid: activeParticipant.uuid,
      accept: true,
      fullName: null,
    });
  };

  const handleCancel = () => cancel();

  const handleSubmit = () => {
    const isDone = primaryParticipantAcceptsComplete;

    disconnect();

    return isDone ? runRouter() : history.push(`/applications/${personalLoanUuid}/funding/intro`);
  };

  const handleTwilioError = (e) => {
    setTwilioError(e.message);
    cancel();
  };

  if (enotaryLoading) return null;

  return (
    <>
      {USER_MEDIA_CHECK &&
        !featuresMissing &&
        personalLoan.status &&
        notarySession?.accessToken && (
          <FundingSession
            notarySession={notarySession}
            notaryComplete={primaryParticipantAcceptsComplete}
            handleAccept={handleAccept}
            handleCancel={handleCancel}
            handleConnected={handleConnected}
            handleSubmit={handleSubmit}
            handleError={handleTwilioError}
            serverError={serverError}
          />
        )}
      <BrowserSupportModal
        header="Video connection error"
        size="sm"
        isOpen={!!twilioError}
        onClose={handleSubmit}
      />
    </>
  );
};

export default FundingSessionContainer;
